import React from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost';

type ButtonProps = {
  children: React.ReactNode;
  variant?: ButtonVariant;
  href?: string;
  download?: boolean;
  className?: string;
  'aria-label'?: string;
  onClick?: () => void;
};

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-emerald-600 text-white hover:bg-emerald-700 shadow-lg shadow-emerald-600/25 hover:shadow-emerald-600/30 dark:bg-emerald-500 dark:hover:bg-emerald-600',
  secondary:
    'bg-stone-200 text-stone-800 hover:bg-stone-300 dark:bg-stone-600 dark:text-stone-200 dark:hover:bg-stone-500',
  outline:
    'border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-500 dark:text-emerald-400 dark:hover:bg-emerald-950/30',
  ghost:
    'text-stone-600 hover:bg-stone-100 dark:text-stone-400 dark:hover:bg-stone-700',
};

export function Button({
  children,
  variant = 'primary',
  href,
  download,
  className = '',
  'aria-label': ariaLabel,
  onClick,
}: ButtonProps) {
  const base =
    'inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2';

  const combined = `${base} ${variantClasses[variant]} ${className}`;

  if (href) {
    return (
      <a
        href={href}
        download={download}
        className={combined}
        aria-label={ariaLabel}
        target={href.startsWith('http') ? '_blank' : undefined}
        rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
      >
        {children}
      </a>
    );
  }

  return (
    <button type="button" className={combined} aria-label={ariaLabel} onClick={onClick}>
      {children}
    </button>
  );
}
